<template>
  <v-card outlined class="mb-4">
    <v-card-title class="text-subtitle-1 py-2">
      {{ $t('config.vehicleOperationStatus.previewTitle') }}
    </v-card-title>
    <v-card-subtitle class="pb-2">
      {{ $t('config.vehicleOperationStatus.previewSubtitle') }}
    </v-card-subtitle>
    <v-card-text class="pt-0">
      <div class="preview-map-container">
        <l-map
          :key="previewMapKey"
          :zoom="previewZoom"
          :center="mapCenter"
          :options="mapOptions"
        >
          <l-tile-layer :url="tileUrl" :attribution="tileAttribution" />
          <l-marker
            v-for="veiculo in previewVeiculos"
            :key="veiculo.id_veiculo"
            :lat-lng="[veiculo.lat, veiculo.lng]"
            :visible="veiculo.visivel === 1"
            :icon="getMarkerIcon(veiculo)"
            :opacity="veiculo.ativo === 1 ? 1 : 0.45"
          >
            <l-tooltip :options="{ direction: 'top', offset: [0, -12] }">
              {{ tooltipLabel(veiculo) }}
            </l-tooltip>
          </l-marker>
        </l-map>
      </div>
      <v-row dense class="mt-3">
        <v-col
          v-for="veiculo in previewVeiculos"
          :key="'legend-' + veiculo.id_veiculo"
          cols="12"
          sm="6"
          md="4"
        >
          <v-chip
            small
            class="ma-1"
            :color="veiculo.visivel === 1 ? veiculo.cor : 'grey lighten-2'"
            :text-color="veiculo.visivel === 1 ? 'white' : 'grey darken-2'"
            :outlined="veiculo.visivel !== 1"
          >
            <span :class="{ 'font-weight-medium': veiculo.ativo !== 1 }">
              {{ veiculo.cod_veiculo }} — {{ veiculo.descricao }}
              <template v-if="veiculo.ativo !== 1">
                ({{ $t('config.vehicleOperationStatus.inactiveLabel') }})
              </template>
              <template v-else-if="veiculo.visivel !== 1">
                ({{ $t('config.vehicleOperationStatus.hiddenOnMap') }})
              </template>
            </span>
          </v-chip>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.preview-map-container {
  width: 100%;
  height: 320px;
  border-radius: 8px;
  overflow: hidden;
  z-index: 0;
}

.blink {
  animation: blink 0.2s infinite alternate;
}

@keyframes blink {
  from {
    opacity: 0;
  }
}
</style>

<script>
import { LMap, LTileLayer, LMarker, LTooltip } from 'vue2-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  buildPreviewVeiculos,
  createVeiculoOperacaoMapIcon,
} from '@/utils/veiculoStatusOperacao';

export default {
  name: 'StatusVeiculosOperacaoMapPreview',
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LTooltip,
  },
  props: {
    statusList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      previewZoom: 15,
      tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      tileAttribution: '&copy; OpenStreetMap contributors',
      mapOptions: {
        scrollWheelZoom: false,
        zoomControl: true,
      },
    };
  },
  computed: {
    previewVeiculos() {
      return buildPreviewVeiculos(this.statusList);
    },
    mapCenter() {
      const veiculos = this.previewVeiculos;
      if (veiculos.length === 0) {
        return [-22.9068, -43.1729];
      }
      const lat = veiculos.reduce((sum, v) => sum + v.lat, 0) / veiculos.length;
      const lng = veiculos.reduce((sum, v) => sum + v.lng, 0) / veiculos.length;
      return [lat, lng];
    },
    previewMapKey() {
      return this.statusList
        .map((s) => `${s.cod_status}:${s.cor}:${s.visivel}:${s.ativo}`)
        .join('|');
    },
  },
  methods: {
    getMarkerIcon(veiculo) {
      return createVeiculoOperacaoMapIcon(veiculo.cor, {
        size: 30,
        blink: veiculo.cod_status === 'gps_offline',
        title: veiculo.cod_veiculo,
      });
    },
    tooltipLabel(veiculo) {
      let label = `${veiculo.cod_veiculo} — ${veiculo.descricao}`;
      if (veiculo.ativo !== 1) {
        label += ` (${this.$t('config.vehicleOperationStatus.inactiveLabel')})`;
      }
      return label;
    },
  },
};
</script>
